/**
 * A tiny bespoke icon set (docs/DESIGN.md anti-generic ban list: "no emoji-as-iconography...
 * Library-default appearance is the single biggest 'made by AI' tell"). Hand-drawn, single-stroke,
 * `currentColor`-based — not an icon library, just the handful of glyphs this app actually needs in
 * place of the wastebasket/money-bag/link/sword/gear emoji the presentation pass had been leaning
 * on. Typographic dingbats that already read as plain UI marks (▲ ▼ ▾ ▸ ‹ › ✓ × ✎) are left alone —
 * the ban is on colour-pictograph emoji standing in for icons, not on ordinary punctuation glyphs.
 */
import type { SVGProps } from 'react';

function IconBase(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 20 20"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    />
  );
}

export function IconTrash(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase {...props}>
      <path d="M4.5 6h11M8 6V4.5h4V6M6 6l.6 9.5A1 1 0 0 0 7.6 16.5h4.8a1 1 0 0 0 1-1.05L14 6M8.3 8.7v5M11.7 8.7v5" />
    </IconBase>
  );
}

export function IconCoin(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase {...props}>
      <circle cx="10" cy="10" r="6" />
      <path d="M10 6.8v6.4M8.2 12c0 .8.8 1.4 1.8 1.4s1.8-.5 1.8-1.3c0-1.7-3.6-1-3.6-2.6 0-.8.8-1.3 1.8-1.3s1.8.5 1.8 1.2" />
    </IconBase>
  );
}

export function IconLink(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase {...props}>
      <path d="M8.3 11.7 11.7 8.3" />
      <path d="M9 6.2 10.4 4.8a2.7 2.7 0 0 1 3.8 3.8L12.8 10" />
      <path d="M11 13.8 9.6 15.2a2.7 2.7 0 0 1-3.8-3.8L7.2 10" />
    </IconBase>
  );
}

export function IconSword(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase {...props}>
      <path d="M5 15 13.5 6.5" />
      <path d="M11.5 4.5 15.5 8.5" />
      <path d="M13.5 6.5 15.5 4.5" />
      <path d="M4.3 15.7 6 14l-.3.3" />
      <path d="M5 15 4 16" />
    </IconBase>
  );
}

export function IconGem(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase {...props}>
      <path d="M6 5h8l2.5 4L10 16.5 3.5 9 6 5Z" />
      <path d="M3.5 9h13M7.7 5 10 9l-1.6 7.3M12.3 5 10 9l1.6 7.3" />
    </IconBase>
  );
}

/** REV7 drag handle — two columns of three dots, the familiar "grip" affordance. */
export function IconGripVertical(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase {...props} strokeWidth={0} fill="currentColor">
      <circle cx="7.5" cy="5.5" r="1.2" />
      <circle cx="7.5" cy="10" r="1.2" />
      <circle cx="7.5" cy="14.5" r="1.2" />
      <circle cx="12.5" cy="5.5" r="1.2" />
      <circle cx="12.5" cy="10" r="1.2" />
      <circle cx="12.5" cy="14.5" r="1.2" />
    </IconBase>
  );
}

/** REV8 `/legion` nav entry — a crest (Maple Union's "Legion" reads as a banner of members). */
export function IconLegion(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase {...props}>
      <path d="M10 3.3 15 5.3v4.4c0 3.4-2.1 6-5 7-2.9-1-5-3.6-5-7V5.3L10 3.3Z" />
      <path d="M10 7.2 10.9 9l2 .3-1.45 1.4.35 2-1.8-.95-1.8.95.35-2L7.1 9.3l2-.3.9-1.8Z" />
    </IconBase>
  );
}

/** REV20 `/hexa` nav entry — a hexagon (the HEXA Matrix's namesake shape), never wiki art in the nav. */
export function IconHexa(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase {...props}>
      <path d="M10 3.3 16 6.9v6.2L10 16.7 4 13.1V6.9L10 3.3Z" />
      <path d="M10 7.4v5M7.7 8.7 12.3 11.3M12.3 8.7 7.7 11.3" />
    </IconBase>
  );
}

/** Sort-by-level (Characters page account cards): descending bars + a down arrow. */
export function IconSortLevel(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase {...props}>
      <path d="M3.5 5.5h8M3.5 10h5.5M3.5 14.5h3" />
      <path d="M15 5v9.5M12.7 12.3 15 14.7l2.3-2.4" />
    </IconBase>
  );
}

export function IconGear(props: SVGProps<SVGSVGElement>) {
  return (
    <IconBase {...props}>
      <circle cx="10" cy="10" r="2.6" />
      <path d="M10 3.8v1.6M10 14.6v1.6M16.2 10h-1.6M5.4 10H3.8M14.4 5.6l-1.1 1.1M6.7 13.3l-1.1 1.1M14.4 14.4l-1.1-1.1M6.7 6.7 5.6 5.6" />
    </IconBase>
  );
}
